import React, { useState, useContext, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import apiClient from '../api/client';

function StaffDashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 bg-gradient-to-b from-green-900 to-green-800 text-white shadow-2xl">
        <div className="p-8 border-b border-green-700">
          <h1 className="text-3xl font-extrabold">🔧 Personel</h1>
          <p className="text-green-200 text-sm mt-3">{user?.full_name}</p>
          <span className="inline-block mt-2 px-3 py-1 bg-green-600 rounded-full text-xs font-semibold">
            {user?.role?.toUpperCase()}
          </span>
        </div>
        
        <nav className="mt-8 space-y-2 px-4">
          <SidebarLink to="/staff/dashboard" icon="📊" label="Dashboard" />
          <SidebarLink to="/staff/orders" icon="🛒" label="Siparişler" />
          <SidebarLink to="/staff/contacts" icon="📞" label="İletişim Talepleri" />
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-md px-8 py-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">Personel Kontrol Paneli</h2>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">Şube: {user?.branch_id}</span>
            <span className="text-gray-300">|</span>
            <span className="text-sm text-gray-600">👤 {user?.full_name}</span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition flex items-center gap-2"
            >
              🚪 Çıkış Yap
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="p-8">
            <Routes>
              <Route path="/dashboard" element={<StaffDashboardHome />} />
              <Route path="/orders" element={<StaffOrders />} />
              <Route path="/contacts" element={<StaffContacts />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarLink({ to, icon, label }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-green-700 transition duration-200 font-medium"
    >
      <span className="text-xl">{icon}</span>
      <span>{label}</span>
    </Link>
  );
}

function StaffDashboardHome() {
  return (
    <div>
      <h3 className="text-3xl font-bold text-gray-800 mb-8">Genel Bakış</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <DashboardCard title="Stokta Ürün" value="0" icon="📦" color="green" />
        <DashboardCard title="Hazırlanıyor" value="0" icon="⚙️" color="yellow" />
        <DashboardCard title="Hazır" value="0" icon="✅" color="blue" />
        <DashboardCard title="İletişim Beklemede" value="0" icon="📞" color="red" />
      </div>
    </div>
  );
}

function DashboardCard({ title, value, icon, color }) {
  const colorClasses = {
    green: 'bg-green-50 border-green-200',
    yellow: 'bg-yellow-50 border-yellow-200',
    blue: 'bg-blue-50 border-blue-200',
    red: 'bg-red-50 border-red-200',
  };

  return (
    <div className={`${colorClasses[color]} border-l-4 rounded-lg p-6 shadow-md hover:shadow-lg transition`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-semibold">{title}</p>
          <p className="text-4xl font-bold text-gray-800 mt-2">{value}</p>
        </div>
        <span className="text-4xl">{icon}</span>
      </div>
    </div>
  );
}

function StaffOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await apiClient.get('/shop/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Siparişler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await apiClient.patch(`/shop/orders/${orderId}/status`, { status: newStatus });
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      alert('Durum güncellenemedi');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'Beklemede', class: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
      preparing: { text: 'Hazırlanıyor', class: 'bg-blue-100 text-blue-800', icon: '⚙️' },
      ready: { text: 'Hazır', class: 'bg-green-100 text-green-800', icon: '✅' },
      completed: { text: 'Tamamlandı', class: 'bg-gray-100 text-gray-800', icon: '📦' },
      cancelled: { text: 'İptal', class: 'bg-red-100 text-red-800', icon: '❌' },
    };
    return badges[status] || badges.pending;
  };

  const filteredOrders = statusFilter 
    ? orders.filter(o => o.status === statusFilter)
    : orders;

  if (loading) return <p className="text-center py-8">Yükleniyor...</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-3xl font-bold text-gray-800">🛒 Siparişler</h3>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="">Tüm Durumlar</option>
            <option value="pending">⏳ Beklemede</option>
            <option value="preparing">⚙️ Hazırlanıyor</option>
            <option value="ready">✅ Hazır</option>
            <option value="completed">📦 Tamamlandı</option>
            <option value="cancelled">❌ İptal</option>
          </select>
          <button 
            onClick={fetchOrders}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            🔄 Yenile
          </button>
        </div>
      </div>

      {/* İstatistikler */}
      <div className="grid grid-cols-5 gap-4">
        {['pending', 'preparing', 'ready', 'completed', 'cancelled'].map(status => {
          const badge = getStatusBadge(status);
          const count = orders.filter(o => o.status === status).length;
          return (
            <div 
              key={status}
              onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
              className={`${badge.class} p-4 rounded-lg text-center cursor-pointer transition hover:opacity-80 ${
                statusFilter === status ? 'ring-2 ring-offset-2 ring-gray-400' : ''
              }`}
            >
              <span className="text-2xl">{badge.icon}</span>
              <p className="font-bold text-2xl">{count}</p>
              <p className="text-sm">{badge.text}</p>
            </div>
          );
        })}
      </div>

      {/* Sipariş Listesi */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Sol Panel - Sipariş Listesi */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 bg-gray-50 border-b font-bold text-gray-700">
            📋 Sipariş Listesi ({filteredOrders.length})
          </div>
          <div className="divide-y max-h-[600px] overflow-auto">
            {filteredOrders.length === 0 ? (
              <p className="p-8 text-center text-gray-500">Sipariş bulunamadı</p>
            ) : (
              filteredOrders.map((order) => {
                const badge = getStatusBadge(order.status);
                return (
                  <div 
                    key={order.id}
                    onClick={() => setSelectedOrder(order)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition ${
                      selectedOrder?.id === order.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-gray-800">#{order.id}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.class}`}>
                        {badge.icon} {badge.text}
                      </span>
                    </div>
                    <p className="font-semibold text-gray-700">{order.customer_name}</p>
                    <p className="text-sm text-gray-500">{order.customer_phone}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleString('tr-TR')}
                      </span>
                      <span className="font-bold text-green-600">{order.total_amount} ₺</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Sağ Panel - Sipariş Detay */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 bg-gray-50 border-b font-bold text-gray-700">
            📦 Sipariş Detayı
          </div>
          {selectedOrder ? (
            <div className="p-6">
              {/* Müşteri Bilgileri */}
              <div className="mb-6">
                <h4 className="font-bold text-lg mb-2">👤 Müşteri Bilgileri</h4>
                <div className="bg-gray-50 p-4 rounded-lg space-y-1">
                  <p><strong>Ad:</strong> {selectedOrder.customer_name}</p>
                  <p><strong>Telefon:</strong> {selectedOrder.customer_phone}</p>
                  {selectedOrder.customer_email && (
                    <p><strong>E-posta:</strong> {selectedOrder.customer_email}</p>
                  )}
                  {selectedOrder.customer_address && (
                    <p><strong>Adres:</strong> {selectedOrder.customer_address}</p>
                  )}
                  {selectedOrder.notes && (
                    <p><strong>Notlar:</strong> {selectedOrder.notes}</p>
                  )}
                </div>
              </div>

              {/* Durum Güncelleme */}
              <div className="mb-6">
                <h4 className="font-bold text-lg mb-2">🔄 Durum Güncelle</h4>
                <div className="flex gap-2 flex-wrap">
                  {['pending', 'preparing', 'ready', 'completed', 'cancelled'].map(status => {
                    const badge = getStatusBadge(status);
                    return (
                      <button
                        key={status}
                        onClick={() => updateStatus(selectedOrder.id, status)}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                          selectedOrder.status === status
                            ? `${badge.class} ring-2 ring-offset-1`
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {badge.icon} {badge.text}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Ürünler */}
              <div>
                <h4 className="font-bold text-lg mb-2">🛍️ Sipariş Ürünleri</h4>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex gap-4 bg-gray-50 p-3 rounded-lg">
                      <img
                        src={`http://localhost:5000${item.image_url}`}
                        alt={item.title}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h5 className="font-bold text-gray-800">{item.title}</h5>
                        {item.barcode && (
                          <span className="text-xs font-mono text-gray-500">🏷️ {item.barcode}</span>
                        )}
                        <div className="flex items-center gap-4 mt-2">
                          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                            {item.quantity} adet
                          </span>
                          {item.price && (
                            <span className="text-green-600 font-bold">{item.price} ₺</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t flex justify-between items-center">
                  <span className="font-bold text-lg">Toplam:</span>
                  <span className="text-2xl font-bold text-green-600">{selectedOrder.total_amount} ₺</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500">
              <span className="text-6xl block mb-4">👈</span>
              <p>Detaylarını görmek için bir sipariş seçin</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StaffContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await apiClient.get('/contact');
      setContacts(response.data);
    } catch (error) {
      console.error('İletişim talepleri yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await apiClient.put(`/contact/${id}`, { status });
      fetchContacts();
    } catch (error) {
      console.error('Durum güncellenemedi:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'Beklemede', class: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
      contacted: { text: 'İletişime Geçildi', class: 'bg-blue-100 text-blue-800', icon: '📞' },
      resolved: { text: 'Çözüldü', class: 'bg-green-100 text-green-800', icon: '✅' }
    };
    return badges[status] || badges.pending;
  };

  const filteredContacts = statusFilter
    ? contacts.filter(c => c.status === statusFilter)
    : contacts;

  if (loading) return <p className="text-center py-8">Yükleniyor...</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-3xl font-bold text-gray-800">📞 İletişim Talepleri</h3>
        <button 
          onClick={fetchContacts}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          🔄 Yenile
        </button>
      </div>

      {/* Durum Filtreleme Kartları */}
      <div className="grid grid-cols-3 gap-4">
        {['pending', 'contacted', 'resolved'].map(status => {
          const badge = getStatusBadge(status);
          const count = contacts.filter(c => c.status === status).length;
          return (
            <div 
              key={status}
              onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
              className={`${badge.class} p-6 rounded-xl text-center cursor-pointer transition-all hover:opacity-80 hover:scale-105 ${statusFilter === status ? 'ring-4 ring-offset-2 ring-gray-400 scale-105' : ''}`}
            >
              <span className="text-4xl block mb-2">{badge.icon}</span>
              <p className="font-bold text-3xl">{count}</p>
              <p className="text-sm font-semibold mt-1">{badge.text}</p>
            </div>
          );
        })}
      </div>

      {/* Toplam ve Aktif Filtre */}
      <div className="flex items-center justify-between">
        <p className="text-gray-600">
          {statusFilter ? (
            <span>Filtrelenen: <strong>{filteredContacts.length}</strong> / {contacts.length} talep</span>
          ) : (
            <span>Toplam: <strong>{contacts.length}</strong> talep</span>
          )}
        </p>
        {statusFilter && (
          <button
            onClick={() => setStatusFilter('')}
            className="text-green-600 hover:text-green-800 text-sm font-semibold"
          >
            ✕ Filtreyi Temizle
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {filteredContacts.length === 0 ? (
          <p className="p-6 text-center text-gray-500">
            {statusFilter ? 'Bu durumda talep bulunamadı' : 'Henüz iletişim talebi yok'}
          </p>
        ) : (
          <div className="divide-y">
            {filteredContacts.map((contact) => {
              const badge = getStatusBadge(contact.status);
              return (
                <div key={contact.id} className="p-6 hover:bg-gray-50 transition">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-bold text-lg text-gray-800">{contact.customer_name}</h4>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.class}`}>
                          {badge.icon} {badge.text}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-2">{contact.message}</p>
                      <div className="flex gap-4 text-sm text-gray-500">
                        {contact.customer_email && <span>📧 {contact.customer_email}</span>}
                        {contact.customer_phone && <span>📱 {contact.customer_phone}</span>}
                        <span>📅 {new Date(contact.created_at).toLocaleDateString('tr-TR')}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <select
                        value={contact.status}
                        onChange={(e) => updateStatus(contact.id, e.target.value)}
                        className="px-3 py-2 border rounded-lg text-sm bg-white"
                      >
                        <option value="pending">⏳ Beklemede</option>
                        <option value="contacted">📞 İletişime Geçildi</option>
                        <option value="resolved">✅ Çözüldü</option>
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default StaffDashboard;
